import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import {
  adjustOrderFreight,
  adjustOrderPromotion,
  adjustOrderUseIntegration,
  cancelOrderByAdmin,
  cancelPendingDeliveryOrder,
  convertOrderToTaxInclusive,
  omsOrderAddOperationNoteV2,
  omsOrderDeliverUpdate,
  omsOrderFinish,
  omsOrderInvalid,
  omsOrderReceiverInfoUpdate,
  omsOrderRevertToPendingDelivery,
  omsOrderRevertToDelivered,
  previewConvertOrderToTaxInclusive,
  updateOmsOrderDetail,
  sendPaymentSuccessEmailByAdmin,
  type ConvertOrderToTaxInclusivePreview,
} from 'apis/oms';
import LOCALS from 'commons/locals';
import { OrderDeliveryTypeMap } from 'commons/options';
import { ORDER_PAYMENT_STATUS_MAP } from 'constant/oms-order-payment-status';
import i18n from 'i18n';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Trans } from 'react-i18next';
import {
  OmsOrder,
  OmsOrderDetail,
  OrderTaxRegime,
  ReceiptDto,
} from 'types/oms';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import { useNavigate } from 'react-router-dom';
import copyToClipboard from 'utils/copyToClipboard';
import { getOrderDetailPageUrl } from 'utils/getPortalOrigin';
import FinalSettlement from './final-settlement';
import { useToggle } from 'react-use';
import { getCityListByCountryCode } from 'apis/home';
import PrintReceiptButton from './print-receipt-button';
import { sumBy } from 'lodash-es';
import { financialManagementAccountList } from 'apis/fms';
import PrintRyoshushoButton from './print-ryoshusho-button';
import { getProductUpdateInfoNew, printReceiptProductName } from 'apis/pms';
import { getMemberById } from 'apis/ums';
import { getReceiptAccessories } from 'pages/prints/receipt/utils';
import SimpleMemberSelect from './simple-member-select';
import useResource from 'commons/hooks/useResource';
import { ProductUpdateInfoNewType } from 'types/pms';
import { STOCK_PLACE_MAP } from 'constant/stock-place';
import { CURRENCY_MAP } from 'constant/currency';
import { getOrderCreateSourceConfig } from 'constant/order-create-source';
import { PRINTABLE_INVOICE_STOCK_PLACE_LIST } from 'constant/oms-order';
import { ORDER_STATUS_MAP } from 'constant/oms-order-status';
import { isShopId, SHOP_OPTION_LIST } from 'constant/shop';
import { LANGUAGE_MAP } from 'constant/language';
import {
  extractClipboardImageFiles,
  fileToUploadFile,
} from 'pages/rrs/recycling-consignment/modal-components/clipboard-image';
import { getBase64 } from 'pages/rrs/recycling-consignment/modal-components/image-utils';
import { uploadOrderImageFiles } from 'utils/order-image-upload';
import { roundMoney } from 'utils/tools';

const DELIVERY_METHOD_MAP = {
  SAGAWA_EXPRESS: 'Sagawa Express',
  EMS: 'EMS',
  DHL: 'DHL',
  FEDEX: 'FedEx',
  SF_Express: 'SF Express',
  LaLa_Move: 'LaLa Move',
  UPS: 'UPS',
  Self_pick_up: 'Self pick-up',
  SingPost: 'SingPost',
};

const DELIVERY_METHOD_OPTION_LIST = Object.values(DELIVERY_METHOD_MAP).map(
  (key) => {
    return {
      value: key,
      label: key,
    };
  },
);

type OrderOperateProps = {
  omsOrderDetail: OmsOrderDetail;
  productList: ProductUpdateInfoNewType[];
  tagList?: { id: number; tag: string }[];
  /** Nest V2 返回；Java 详情无此字段 */
  taxRegime?: OrderTaxRegime | null;
};

const OrderInvalidButton = ({ id }: { id: OmsOrder['id'] }) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  return (
    <>
      <Modal
        title={i18n.t('invalid_order')}
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        onOk={async () => {
          form.validateFields().then(async ({ note, autoPublish }) => {
            await omsOrderInvalid(id, note, autoPublish);
            window.location.reload();
          });
        }}
      >
        <p className="text-red-500 text-base">
          {i18n.t('confirm_invalid_order_change')}
        </p>
        <p className="text-red-500 text-base">
          {i18n.t('invalid_order_points_adjustment')}
        </p>
        <div>
          <Form form={form}>
            <Form.Item
              name="note"
              rules={[
                {
                  required: true,
                  message: <Trans i18nKey={LOCALS.please_enter} />,
                },
              ]}
            >
              <Input.TextArea
                placeholder={i18n.t('cancellation_reason') || ''}
              />
            </Form.Item>
          </Form>
        </div>
        <div>
          <Form form={form}>
            <Form.Item name="autoPublish" label={i18n.t('auto_publish')}>
              <Switch />
            </Form.Item>
          </Form>
        </div>
      </Modal>
      <Button
        danger
        type="primary"
        onClick={() => {
          setOpen(true);
        }}
      >
        {i18n.t(LOCALS.invalid_order)}
      </Button>
    </>
  );
};

const PrintOrderInvoiceButton = ({
  omsOrderDetail,
  productList,
}: {
  omsOrderDetail: OmsOrderDetail;
  productList: ProductUpdateInfoNewType[];
}) => {
  if (!productList.length) return null;
  if (!omsOrderDetail.deliveryTime) return null;

  const productStockPlaceList = productList.map((i) => i.stockPlace);
  const isAllProductStockPlaceSame = productStockPlaceList.every(
    (i) => i === productStockPlaceList[0],
  );
  if (!isAllProductStockPlaceSame) return null;

  if (
    !PRINTABLE_INVOICE_STOCK_PLACE_LIST.includes(
      productStockPlaceList[0] as (typeof PRINTABLE_INVOICE_STOCK_PLACE_LIST)[number],
    )
  ) {
    return null;
  }

  const printPath = `/prints/order-invoice-v2?orderIdList=${omsOrderDetail.id.toString()}`;

  return (
    <Button
      onClick={() => {
        window.open(printPath);
      }}
    >
      {i18n.t('print_sg_order_invoice')}
    </Button>
  );
};

const OrderOperate = ({
  omsOrderDetail,
  productList,
  tagList = [],
  taxRegime,
}: OrderOperateProps) => {
  const {
    id,
    status,
    // 收货人信息
    receiverName,
    receiverPhone,
    receiverPostCode,
    receiverProvince,
    receiverCity,
    receiverDetailAddress,
    orderType,
    createdFrom,
    memberId,
    useIntegration,
    payAmount,
    payAmountActualCurrency,
    payList,
    orderItemList,
    // 邮寄信息
    deliveryCompany,
    deliverySn,
    deliveryType,
  } = omsOrderDetail;
  const { countryOptions, accessorySelectOptions } =
    useAppSelector(selectGlobalInfo);
  const { isAdmin } = useAppSelector(selectUserInfo);
  const navigate = useNavigate();
  const canInvalidOrder = useResource('order-detail-order-invalid');
  const isB2BOrder = tagList.some((i) => i.tag === 'B2B');
  const [
    modifyRecipientInformationModalOpen,
    setModifyRecipientInformationModalOpen,
  ] = useState(false);
  const [
    modifyMailingInformationModalOpen,
    setModifyMailingInformationModalOpen,
  ] = useState(false);
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [completeOrderModalOpen, setCompleteOrderModalOpen] = useState(false);
  const [closeOrderModalOpen, setCloseOrderModalOpen] = useState(false);
  const [adjustPromotionModalOpen, setAdjustPromotionModalOpen] =
    useState(false);
  const [adjustPromotionValue, setAdjustPromotionValue] = useState(0);
  const [adjustPromotionLoading, setAdjustPromotionLoading] = useState(false);
  const [adjustFreightModalOpen, setAdjustFreightModalOpen] = useState(false);
  const [adjustFreightValue, setAdjustFreightValue] = useState(0);
  const [adjustFreightLoading, setAdjustFreightLoading] = useState(false);
  const [convertTaxModalOpen, setConvertTaxModalOpen] = useState(false);
  const [convertTaxLoading, setConvertTaxLoading] = useState(false);
  const [convertTaxPreviewLoading, setConvertTaxPreviewLoading] =
    useState(false);
  const [convertTaxPreview, setConvertTaxPreview] =
    useState<ConvertOrderToTaxInclusivePreview | null>(null);
  const [appendIntegrationModalOpen, setAppendIntegrationModalOpen] =
    useState(false);
  const [appendIntegrationValue, setAppendIntegrationValue] = useState<
    number | null
  >(null);
  const [appendIntegrationLoading, setAppendIntegrationLoading] =
    useState(false);
  const [memberAvailableIntegration, setMemberAvailableIntegration] = useState<
    number | null
  >(null);
  const [bindMemberModalOpen, setBingMemberModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number>();
  const [
    customerInfoRecordTemplateChecked,
    setCustomerInfoRecordTemplateChecked,
  ] = useState(false);
  const [operationFileList, setOperationFileList] = useState<UploadFile[]>([]);
  const customerInfoRecordHiddenPrefixRef = useRef<string>('');

  const [recipientInformationForm] = Form.useForm();
  const [mailingInformationForm] = Form.useForm();
  const [remarkForm] = Form.useForm();
  const [shipForm] = Form.useForm();
  const [completeOrderForm] = Form.useForm();
  const [closeOrderForm] = Form.useForm();

  const [finalSettlementModalOpen, toggleFinalSettlementModalOpen] =
    useToggle(false);

  const actualCurrencyRate = orderItemList?.[0]?.actualCurrencyRate ?? 1;
  const isJpyOrder = (orderItemList || []).every(
    (item) => item.actualCurrency === CURRENCY_MAP.JPY,
  );
  const isAllTaxFree =
    !!orderItemList?.length &&
    orderItemList.every((item) => item.isTaxFree === 1);
  const currentUseIntegration = useIntegration ?? 0;

  const maxAppendIntegration = useMemo(() => {
    const maxFromPayAmount = Math.max(0, payAmount ?? 0);

    const paidAmountActual = sumBy(
      payList?.filter((i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID) ??
        [],
      (i) => i.payAmount,
    );
    const remainingPayableActual = Math.max(
      0,
      (payAmountActualCurrency ?? 0) - paidAmountActual,
    );
    const maxFromPaid =
      actualCurrencyRate > 0
        ? Math.floor(remainingPayableActual / actualCurrencyRate)
        : maxFromPayAmount;

    const limits = [maxFromPayAmount, maxFromPaid];
    if (memberAvailableIntegration != null) {
      limits.push(memberAvailableIntegration);
    }

    return Math.max(0, Math.min(...limits));
  }, [
    actualCurrencyRate,
    memberAvailableIntegration,
    payAmount,
    payAmountActualCurrency,
    payList,
  ]);

  useEffect(() => {
    if (!appendIntegrationModalOpen || !memberId) {
      return;
    }

    let cancelled = false;
    getMemberById(memberId)
      .then((res) => {
        if (!cancelled) {
          setMemberAvailableIntegration(res.data?.integration ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMemberAvailableIntegration(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appendIntegrationModalOpen, memberId]);

  const onModifyRecipientInformationOk = useCallback(() => {
    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        const data = recipientInformationForm.getFieldsValue();

        await omsOrderReceiverInfoUpdate({
          orderId: id,
          status,
          ...data,
        });

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id, recipientInformationForm, status]);

  const onModifyMailingInformationOk = useCallback(() => {
    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        const data = mailingInformationForm.getFieldsValue();

        await omsOrderDeliverUpdate([
          {
            orderId: id,
            ...data,
          },
        ]);

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id, mailingInformationForm]);

  useEffect(() => {
    if (!remarkModalOpen) return;

    const onPaste = (event: globalThis.ClipboardEvent) => {
      const images = extractClipboardImageFiles(event);
      if (images.length === 0) return;

      event.preventDefault();
      const newFiles = images.map((image) => fileToUploadFile(image));
      setOperationFileList((previous) => [...previous, ...newFiles]);
      message.success(
        i18n.t(LOCALS.payment_voucher_clipboard_paste_success, {
          count: images.length,
          mergeNotice: '',
        }),
      );
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [remarkModalOpen]);

  const handleOperationFileChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      const normalized = info.fileList.map((file) =>
        file.originFileObj ? { ...file, status: 'done' as const } : file,
      );
      setOperationFileList(normalized);
    },
    [],
  );

  const handleOperationFilePreview = useCallback(async (file: UploadFile) => {
    if (file.url) {
      window.open(file.url);
      return;
    }
    if (file.originFileObj) {
      const source = await getBase64(file.originFileObj as RcFile);
      const image = new window.Image();
      image.src = source;
      const imageWindow = window.open(source);
      imageWindow?.document.write(image.outerHTML);
    }
  }, []);

  const normalizeOrderNotePresets = useCallback((note: string): string => {
    const presetMap = [
      { key: LOCALS.gift_with_purchase, storedValue: 'gift_with_purchase' },
    ];
    return presetMap.reduce((result, { key, storedValue }) => {
      return ['en', 'ja', 'zh_CN'].reduce((acc, lng) => {
        const localized = i18n.t(key, { lng });
        return localized && localized !== storedValue
          ? acc.replaceAll(localized, storedValue)
          : acc;
      }, result);
    }, note);
  }, []);

  const onRemarkOk = useCallback(async () => {
    const data = await remarkForm.validateFields();
    const currentFileList = [...operationFileList];

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        let operationImages: string | undefined;
        let operationImagesUrl: string | undefined;
        const imageFiles = currentFileList
          .filter((file) => file.originFileObj instanceof Blob)
          .map((file) => file.originFileObj as File);

        if (imageFiles.length > 0) {
          try {
            const { keys, urls } = await uploadOrderImageFiles(id, imageFiles);
            operationImages = keys;
            operationImagesUrl = urls;
          } catch {
            message.error(i18n.t(LOCALS.ai_identify_upload_failed));
            return;
          }
        }

        const hiddenPrefix = customerInfoRecordHiddenPrefixRef.current;
        const noteWithPrefix =
          customerInfoRecordTemplateChecked && hiddenPrefix
            ? `${hiddenPrefix}\n${data.note ?? ''}`
            : data.note;
        await omsOrderAddOperationNoteV2({
          id,
          status,
          ...data,
          note: noteWithPrefix
            ? normalizeOrderNotePresets(noteWithPrefix)
            : noteWithPrefix,
          operationImages,
          operationImagesUrl,
          isSendNotice: customerInfoRecordTemplateChecked || undefined,
        });

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [
    customerInfoRecordTemplateChecked,
    id,
    normalizeOrderNotePresets,
    operationFileList,
    remarkForm,
    status,
  ]);

  const getCreatedFromLabel = () => {
    return getOrderCreateSourceConfig(createdFrom).label;
  };

  const buildCustomerInfoRecordTemplate = () => {
    const productNumbers = Array.from(
      new Set(omsOrderDetail.orderItemList.map((item) => item.productId)),
    ).join('\n');
    const hasTaxFree = omsOrderDetail.orderItemList.some(
      (item) => item.isTaxFree,
    );
    const taxFreeText = hasTaxFree
      ? i18n.t(LOCALS.tax_free_present)
      : i18n.t(LOCALS.tax_free_absent);

    const hidden = [
      i18n.t(LOCALS.customer_info_record_staff_label),
      omsOrderDetail.staffName || '',
      i18n.t(LOCALS.customer_info_record_product_number_label),
      productNumbers,
      i18n.t(LOCALS.customer_info_record_store_label),
      getCreatedFromLabel(),
      i18n.t(LOCALS.customer_info_record_tax_free_label),
      taxFreeText,
    ].join('\n');

    const visible = [
      i18n.t(LOCALS.customer_info_record_country_residence_label),
      '',
      i18n.t(LOCALS.customer_info_record_visit_reason_label),
      '',
      i18n.t(LOCALS.customer_info_record_first_channel_label),
      '',
      i18n.t(LOCALS.customer_info_record_ai_search_label),
      '',
      i18n.t(LOCALS.customer_info_record_sns_account_label),
      '',
      i18n.t(LOCALS.customer_info_record_buyer_show_label),
      '',
      i18n.t(LOCALS.customer_info_record_official_site_label),
      '',
      i18n.t(LOCALS.customer_info_record_preference_label),
      '',
      i18n.t(LOCALS.customer_info_record_detail_label),
      '',
      i18n.t(LOCALS.customer_info_record_owner_label),
      '',
    ].join('\n');

    return { hidden, visible };
  };

  const onCustomerInfoRecordTemplateChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    setCustomerInfoRecordTemplateChecked(checked);

    if (checked) {
      const { hidden, visible } = buildCustomerInfoRecordTemplate();
      customerInfoRecordHiddenPrefixRef.current = hidden;
      remarkForm.setFieldValue('note', visible);
    } else {
      customerInfoRecordHiddenPrefixRef.current = '';
      remarkForm.setFieldValue('note', '');
    }
  };

  const onShipOk = useCallback(async () => {
    const data = await shipForm.validateFields();
    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        await omsOrderDeliverUpdate([
          {
            orderId: id,
            ...data,
          },
        ]);

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id, shipForm]);

  const onCompleteOrderOk = useCallback(async () => {
    const data = await completeOrderForm.validateFields();

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        await omsOrderFinish({
          orderId: id,
          ...data,
        });

        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [completeOrderForm, id]);

  const onRevertToPendingDelivery = useCallback(() => {
    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_revert_to_pending_delivery} />,
      onOk: async () => {
        await omsOrderRevertToPendingDelivery(id);
        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id]);

  const onRevertToDelivered = useCallback(() => {
    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_revert_to_delivered} />,
      onOk: async () => {
        await omsOrderRevertToDelivered(id);
        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id]);

  const onCancelPendingDelivery = useCallback(() => {
    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_cancel_pending_delivery} />,
      onOk: async () => {
        await cancelPendingDeliveryOrder(id);
        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [id]);

  const onCloseOrderOk = useCallback(async () => {
    const data = await closeOrderForm.validateFields();

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        await cancelOrderByAdmin({ id, ...data });
        message.success(i18n.t(LOCALS.successful_operation));
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  }, [closeOrderForm, id]);

  // 通过浏览器打印小票,在连接不上打印机下的兜底方案
  const onPrintReceiptByBrowser = useCallback(
    async ({ addSign = false }: { addSign?: boolean } = {}) => {
      const receiptLang = (() => {
        const actualCurrency = omsOrderDetail.orderItemList[0]?.actualCurrency;
        if (actualCurrency === CURRENCY_MAP.JPY) return LANGUAGE_MAP.JA;
        if (actualCurrency === CURRENCY_MAP.CNY) return LANGUAGE_MAP.ZH_CN;
        return LANGUAGE_MAP.EN;
      })();

      const [
        productNameMap,
        productInfoList,
        paymentAccountList,
        memberDetail,
      ] = await Promise.all([
        printReceiptProductName(
          omsOrderDetail.orderItemList.map((i) => i.productId),
        ),
        Promise.all(
          omsOrderDetail.orderItemList.map((item) =>
            getProductUpdateInfoNew(item.productId),
          ),
        ),
        financialManagementAccountList(),
        omsOrderDetail.memberId ? getMemberById(omsOrderDetail.memberId) : null,
      ]);

      // TODO: 有点 hack 的方法，需要优化
      paymentAccountList.push({
        id: '99999',
        accountCode: 'CRYPTO_CURRENCY_TRIPLE_A',
        accountName: '暗号通貨',
        accountDescribe: 'Crypto Currency',
        status: 0,
        storeId: 1,
        deleteStatus: 0,
        sort: 0,
        isCash: 0,
      });

      const cumulativePoints = memberDetail?.data?.integration || 0;
      const productInfoMap = new Map(
        productInfoList.map((productInfo) => [productInfo.id, productInfo]),
      );

      const omsOrderItems = omsOrderDetail.orderItemList.map(
        ({
          productSn,
          productBrand,
          realAmountCurrency,
          taxAmount,
          productPrice,
          i18n,
          isTaxFree,
          productId,
          productPriceActualCurrency,
          productQuantity,
        }) => {
          const productInfo = productInfoMap.get(productId);

          return {
            productSn,
            productBrand,
            productName: productNameMap[productId].join('\n'),
            realAmountCurrency,
            taxAmount,
            productPrice,
            isTaxFree,
            productId,
            productPriceActualCurrency,
            productQuantity,
            accessories: getReceiptAccessories(
              productInfo?.accessory,
              receiptLang,
              accessorySelectOptions,
            ),
          };
        },
      );

      const receivedAmount = (() => {
        if (omsOrderDetail.payList && omsOrderDetail.payList.length) {
          return sumBy(
            omsOrderDetail.payList.filter(
              (i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID,
            ),
            (i) => i.payAmount,
          );
        }

        if (omsOrderDetail.status === ORDER_STATUS_MAP.TO_BE_PAID) {
          return 0;
        }

        // 这里主要是兼容一些没有支付记录，但是有实际收款金额的订单
        return omsOrderDetail.payAmountActualCurrency || 0;
      })();

      const payList = omsOrderDetail.payList
        ?.filter((i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID)
        .map((i) => ({
          name:
            paymentAccountList.find((d) => d.accountCode === i.payType)
              ?.accountName || i.payType,
          amount: i.payAmount,
        }));

      const body: ReceiptDto = {
        id: omsOrderDetail.id,
        createdFrom: omsOrderDetail.createdFrom,
        staffName: omsOrderDetail.staffName,
        orderSn: omsOrderDetail.orderSn,
        integration: omsOrderDetail.integration,
        useIntegration: omsOrderDetail.integrationAmount,
        promotionAmount: omsOrderDetail.promotionAmountActualCurrency || 0,
        createTime: omsOrderDetail.createTime,
        totalAmountActualCurrency: omsOrderDetail.totalAmountActualCurrency,
        payAmountActualCurrency: omsOrderDetail.payAmountActualCurrency,
        omsOrderItems,
        couponAmount: omsOrderDetail.couponAmount,
        totalTaxAmount: omsOrderDetail.totalTaxAmount,
        taxRegime,
        receivedAmount,
        changeAmount: roundMoney(
          receivedAmount - (omsOrderDetail.payAmountActualCurrency || 0),
        ),
        payList,
        paymentTime: omsOrderDetail.paymentTime,
        memberId: omsOrderDetail.memberId,
        stockPlace: productInfoList[0]?.stockPlace || STOCK_PLACE_MAP.JAPAN,
        cumulativePoints,
        deliveryTime: omsOrderDetail.deliveryTime,
        freightAmountActualCurrency: omsOrderDetail.freightAmountActualCurrency,
        orderType: omsOrderDetail.orderType,
        status: omsOrderDetail.status,
        actualCurrency: omsOrderDetail.orderItemList[0]?.actualCurrency,
        addSign,
      };
      const encode = encodeURIComponent(JSON.stringify(body));
      window.open(`/prints/receipt?body=${encode}`);
    },
    [omsOrderDetail, accessorySelectOptions, taxRegime],
  );

  const receiverProvinceValue = Form.useWatch(
    'receiverProvince',
    recipientInformationForm,
  );
  const [cityList, setCityList] = useState<
    {
      code: string;
      countryCode: string;
      id: string;
      name: string;
    }[]
  >([]);
  useEffect(() => {
    if (!receiverProvinceValue) return;
    getCityListByCountryCode(receiverProvinceValue).then((res) => {
      if (!res.data) return;

      const { cityList } = res.data;

      if (cityList) setCityList(cityList);
    });
  }, [receiverProvinceValue]);
  const [bindMemberForm] = Form.useForm();

  const onBindMemberOk = useCallback(async () => {
    if (!selectedMemberId) {
      return;
    }
    const formData = await bindMemberForm.validateFields();

    await updateOmsOrderDetail(id, {
      memberId: selectedMemberId,
      isPointsGiven: formData.isPointsGiven,
    });

    message.success(i18n.t(LOCALS.successful_operation));
    setBingMemberModalOpen(false);
    bindMemberForm.resetFields();
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [id, selectedMemberId, bindMemberForm]);

  // 发送支付成功邮件
  const onSendPaymentSuccessEmail = useCallback(async () => {
    Modal.confirm({
      title: '确认发送支付成功邮件',
      content: '确定要给该订单发送支付成功邮件通知吗？',
      onOk: async () => {
        try {
          await sendPaymentSuccessEmailByAdmin(id);
          message.success('邮件发送成功');
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error) {
          message.error('邮件发送失败');
        }
      },
    });
  }, [id]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center justify-between">
        <Space className="flex flex-wrap">
          <Button
            onClick={() => {
              navigate(-1);
            }}
          >
            <Trans i18nKey={LOCALS.back} />
          </Button>

          {/* 订单备注按钮 */}
          <Button
            onClick={() => {
              setRemarkModalOpen(true);
            }}
          >
            <Trans i18nKey={LOCALS.remark} />
          </Button>

          {/* 复制订单支付链接按钮 */}
          {status === ORDER_STATUS_MAP.TO_BE_PAID && (
            <Button
              onClick={() => {
                copyToClipboard(getOrderDetailPageUrl(id));
                message.success(i18n.t(LOCALS.kTFIViDbSC));
              }}
            >
              {<Trans i18nKey={LOCALS.vDnOozVzna} />}
            </Button>
          )}
        </Space>

        <Space className="flex flex-wrap">
          {/* 添加绑定会员按钮 */}
          {!omsOrderDetail.memberId && (
            <Button onClick={() => setBingMemberModalOpen(true)}>
              <Trans i18nKey={LOCALS.UFbYgUaLbn} />
            </Button>
          )}

          {/* 修改邮寄信息按钮 */}
          {ORDER_STATUS_MAP.DELIVERED === status && (
            <Button
              onClick={() => {
                setModifyMailingInformationModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.modify_mailing_information} />
            </Button>
          )}

          {/* 修改收件人信息按钮 */}

          {(ORDER_STATUS_MAP.TO_BE_PAID === status ||
            ORDER_STATUS_MAP.TO_BE_DELIVERED === status) && (
            <Button
              type="primary"
              onClick={() => {
                setModifyRecipientInformationModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.modify_recipient_information} />
            </Button>
          )}

          {/* 调整优惠按钮 - 仅 admin；待付款可增减，待发货/已发货仅可增加（退差价） */}
          {isAdmin &&
            (status === ORDER_STATUS_MAP.TO_BE_PAID ||
              status === ORDER_STATUS_MAP.TO_BE_DELIVERED ||
              status === ORDER_STATUS_MAP.DELIVERED) && (
              <Button
                onClick={() => {
                  setAdjustPromotionValue(
                    omsOrderDetail.promotionAmountActualCurrency ??
                      omsOrderDetail.promotionAmount ??
                      0,
                  );
                  setAdjustPromotionModalOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.adjust_promotion} />
              </Button>
            )}

          {/* 调整运费按钮 - 仅 isAdmin、待付款 */}
          {isAdmin && status === ORDER_STATUS_MAP.TO_BE_PAID && (
            <Button
              onClick={() => {
                setAdjustFreightValue(
                  omsOrderDetail.freightAmountActualCurrency ??
                    omsOrderDetail.freightAmount ??
                    0,
                );
                setAdjustFreightModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.adjust_freight} />
            </Button>
          )}

          {/* 待发货、已发货、已完成的日元免税订单改为含税价，生成补差价 */}
          {isAdmin &&
            (status === ORDER_STATUS_MAP.TO_BE_DELIVERED ||
              status === ORDER_STATUS_MAP.DELIVERED ||
              status === ORDER_STATUS_MAP.COMPLETED) &&
            isJpyOrder &&
            isAllTaxFree && (
              <Button
                onClick={async () => {
                  setConvertTaxModalOpen(true);
                  setConvertTaxPreview(null);
                  setConvertTaxPreviewLoading(true);
                  try {
                    const data = await previewConvertOrderToTaxInclusive(id);
                    setConvertTaxPreview(data);
                  } catch {
                    setConvertTaxModalOpen(false);
                  } finally {
                    setConvertTaxPreviewLoading(false);
                  }
                }}
              >
                <Trans i18nKey={LOCALS.convert_to_tax_inclusive} />
              </Button>
            )}

          {/* 追加使用积分 - 仅 admin、待付款且有会员 */}
          {isAdmin && status === ORDER_STATUS_MAP.TO_BE_PAID && memberId && (
            <Button
              onClick={() => {
                setAppendIntegrationValue(null);
                setMemberAvailableIntegration(null);
                setAppendIntegrationModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.append_use_integration} />
            </Button>
          )}

          {/* 发货按钮 */}
          {ORDER_STATUS_MAP.TO_BE_DELIVERED === status && (
            <Button
              onClick={() => {
                setShipModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.ship} />
            </Button>
          )}

          {/* 订单完成按钮 */}
          {ORDER_STATUS_MAP.DELIVERED === status && (
            <Button
              onClick={() => {
                setCompleteOrderModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.complete_order} />
            </Button>
          )}

          {/* 退回待发货（纠错） - 仅 admin */}
          {isAdmin && ORDER_STATUS_MAP.DELIVERED === status && (
            <Button onClick={onRevertToPendingDelivery}>
              <Trans i18nKey={LOCALS.revert_to_pending_delivery} />
            </Button>
          )}

          {/* 结算尾款 */}
          {ORDER_STATUS_MAP.TO_BE_PAID === status &&
            orderType === 2 &&
            isShopId(createdFrom) &&
            SHOP_OPTION_LIST.map((i) => i.value).includes(createdFrom) && (
              <Button
                type="primary"
                onClick={() => {
                  toggleFinalSettlementModalOpen(true);
                }}
              >
                <Trans i18nKey={LOCALS.final_payment_settlement} />
              </Button>
            )}

          {(ORDER_STATUS_MAP.TO_BE_DELIVERED === status ||
            ORDER_STATUS_MAP.DELIVERED === status ||
            ORDER_STATUS_MAP.COMPLETED === status ||
            (ORDER_STATUS_MAP.TO_BE_PAID === status && orderType === 2)) && ( // 支付了部分的定金订单也能打印小票
            <>
              <PrintReceiptButton
                omsOrderDetail={omsOrderDetail}
                onPrintReceiptByBrowser={onPrintReceiptByBrowser}
                isB2B={isB2BOrder}
              />
            </>
          )}

          {status === ORDER_STATUS_MAP.COMPLETED && (
            <PrintRyoshushoButton
              omsOrderDetail={omsOrderDetail}
              productList={productList}
            />
          )}

          {/* 待发货直接取消 - 仅 admin，订单变为已取消，商品售卖中不上架 */}
          {isAdmin && ORDER_STATUS_MAP.TO_BE_DELIVERED === status && (
            <Button danger onClick={onCancelPendingDelivery}>
              <Trans i18nKey={LOCALS.cancel_order} />
            </Button>
          )}

          {/* 关闭订单按钮 */}
          {(ORDER_STATUS_MAP.TO_BE_PAID === status ||
            ORDER_STATUS_MAP.TO_BE_DELIVERED === status ||
            ORDER_STATUS_MAP.DELIVERED === status) && (
            <Button
              danger
              type="primary"
              onClick={() => {
                setCloseOrderModalOpen(true);
              }}
            >
              <Trans i18nKey={LOCALS.close_order} />
            </Button>
          )}

          {/* <PrintSGOrderInvoiceButton
            omsOrderDetail={omsOrderDetail}
            productList={productList}
          /> */}

          <PrintOrderInvoiceButton
            omsOrderDetail={omsOrderDetail}
            productList={productList}
          />

          {/* 退回已发货 - 仅 admin */}
          {isAdmin && status === ORDER_STATUS_MAP.COMPLETED && (
            <Button onClick={onRevertToDelivered}>
              <Trans i18nKey={LOCALS.revert_to_delivered} />
            </Button>
          )}

          {status === ORDER_STATUS_MAP.COMPLETED && canInvalidOrder && (
            <OrderInvalidButton id={id} />
          )}

          {/* 发送支付成功邮件按钮 - 仅 admin 可见 */}
          {isAdmin && omsOrderDetail.paymentTime && omsOrderDetail.memberId && (
            <Button onClick={onSendPaymentSuccessEmail}>
              发送支付成功邮件
            </Button>
          )}
        </Space>
      </div>

      <Modal
        title={<Trans i18nKey={LOCALS.modify_mailing_information} />}
        open={modifyMailingInformationModalOpen}
        onOk={onModifyMailingInformationOk}
        onCancel={() => {
          setModifyMailingInformationModalOpen(false);
          mailingInformationForm.resetFields();
        }}
        width={600}
      >
        <Form
          labelCol={{ span: 6 }}
          form={mailingInformationForm}
          initialValues={{
            deliveryCompany,
            deliverySn,
            isSendEmail: true,
          }}
        >
          <Form.Item
            name="deliveryCompany"
            label={<Trans i18nKey={LOCALS.delivery_method} />}
            rules={[{ required: true }]}
          >
            <Select
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              options={DELIVERY_METHOD_OPTION_LIST}
            ></Select>
          </Form.Item>

          <Form.Item
            name="deliverySn"
            label={<Trans i18nKey={LOCALS.shipment_number} />}
            rules={[{ required: true }]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
          <Form.Item
            name="isSendEmail"
            label={i18n.t('HVTbgZaqUs')}
            valuePropName="checked"
          >
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<Trans i18nKey={LOCALS.modify_recipient_information} />}
        open={modifyRecipientInformationModalOpen}
        onOk={onModifyRecipientInformationOk}
        onCancel={() => {
          setModifyRecipientInformationModalOpen(false);
          recipientInformationForm.resetFields();
        }}
        width={700}
      >
        <Form
          labelCol={{ span: 6 }}
          form={recipientInformationForm}
          initialValues={{
            receiverName,
            receiverPhone,
            receiverPostCode,
            receiverProvince,
            receiverCity,
            receiverDetailAddress,
          }}
        >
          <Form.Item
            name="receiverName"
            label={<Trans i18nKey={LOCALS.name} />}
            rules={[{ required: true }]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>

          <Form.Item
            name="receiverProvince"
            label={<Trans i18nKey={LOCALS.country_region} />}
            rules={[{ required: true }]}
          >
            <Select
              onChange={() => {
                recipientInformationForm.setFieldValue('receiverCity', '');
              }}
              options={countryOptions}
              showSearch
              filterOption={(input: string, option?: any) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="receiverCity"
            label={<Trans i18nKey={LOCALS.state_province_city} />}
          >
            <Select
              allowClear
              options={cityList.map((i) => {
                return {
                  value: i.code,
                  label: i.name,
                };
              })}
              showSearch
              filterOption={(input: string, option?: any) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="receiverDetailAddress"
            label={<Trans i18nKey={LOCALS.detail_address} />}
            rules={[{ required: true }]}
          >
            <Input.TextArea
              rows={4}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item
            name="receiverPhone"
            label={<Trans i18nKey={LOCALS.phone_number} />}
            rules={[{ required: true }]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>

          <Form.Item
            name="receiverPostCode"
            label={<Trans i18nKey={LOCALS.zip_code} />}
            rules={[{ required: true }]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={shipModalOpen}
        title={<Trans i18nKey={LOCALS.ship} />}
        onCancel={() => {
          setShipModalOpen(false);
          shipForm.resetFields();
        }}
        onOk={onShipOk}
        width={500}
      >
        <Form
          form={shipForm}
          labelCol={{ span: 8 }}
          initialValues={{
            deliveryCompany:
              deliveryType === OrderDeliveryTypeMap.PICKUP
                ? DELIVERY_METHOD_MAP.Self_pick_up
                : '',
            isSendEmail: true,
          }}
        >
          <Form.Item
            label={<Trans i18nKey={LOCALS.delivery_method} />}
            name="deliveryCompany"
            rules={[{ required: true }]}
          >
            <Select
              disabled={deliveryType === OrderDeliveryTypeMap.PICKUP}
              options={DELIVERY_METHOD_OPTION_LIST}
              placeholder={i18n.t(LOCALS.please_select)}
            ></Select>
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.shipment_number} />}
            name="deliverySn"
            rules={[{ required: deliveryType !== OrderDeliveryTypeMap.PICKUP }]}
          >
            <Input
              disabled={deliveryType === OrderDeliveryTypeMap.PICKUP}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>
          <Form.Item
            name="isSendEmail"
            label={i18n.t('HVTbgZaqUs')}
            valuePropName="checked"
          >
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={completeOrderModalOpen}
        title={<Trans i18nKey={LOCALS.complete_order} />}
        onCancel={() => {
          setCompleteOrderModalOpen(false);
        }}
        onOk={onCompleteOrderOk}
      >
        <Form form={completeOrderForm} labelCol={{ span: 6 }}>
          <Form.Item
            name="isPointsGiven"
            label={<Trans i18nKey={LOCALS.points_earned} />}
            initialValue={true}
          >
            <Radio.Group>
              <Radio value={true}>
                <Trans i18nKey={LOCALS.yes} />
              </Radio>
              <Radio value={false}>
                <Trans i18nKey={LOCALS.no} />
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            rules={[{ required: true }]}
            name="note"
            label={<Trans i18nKey={LOCALS.remark} />}
          >
            <Input.TextArea placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={remarkModalOpen}
        title={<Trans i18nKey={LOCALS.operation_remark} />}
        onCancel={() => {
          setRemarkModalOpen(false);
          remarkForm.resetFields();
          setCustomerInfoRecordTemplateChecked(false);
          setOperationFileList([]);
        }}
        onOk={onRemarkOk}
        width={700}
      >
        <Form form={remarkForm} labelCol={{ span: 4 }}>
          <Form.Item
            name="note"
            label={<Trans i18nKey={LOCALS.remark} />}
            rules={[{ required: true }]}
          >
            <Input.TextArea
              autoSize={
                customerInfoRecordTemplateChecked
                  ? { minRows: 16, maxRows: 24 }
                  : undefined
              }
              rows={customerInfoRecordTemplateChecked ? undefined : 4}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>
          {customerInfoRecordTemplateChecked && (
            <div className="ml-[16.66%] -mt-4 mb-4 text-xs text-gray-400">
              <Trans i18nKey={LOCALS.remark_template_default_filled_hint} />
            </div>
          )}
          <Form.Item label={<Trans i18nKey={LOCALS.related_images} />}>
            <Upload
              accept="image/*"
              listType="picture-card"
              beforeUpload={() => false}
              fileList={operationFileList}
              onChange={handleOperationFileChange}
              onPreview={handleOperationFilePreview}
              multiple
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              <UploadOutlined />
            </Upload>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 6 }}>
              {i18n.t(LOCALS.operation_remark_upload_tip)}
            </div>
          </Form.Item>
        </Form>
        <div className="mt-2 flex items-center gap-4">
          <Checkbox
            checked={customerInfoRecordTemplateChecked}
            onChange={onCustomerInfoRecordTemplateChange}
          >
            <Trans i18nKey={LOCALS.customer_info_record_template} />
          </Checkbox>
          <Button
            size="small"
            onClick={() => {
              remarkForm.setFieldValue(
                'note',
                i18n.t(LOCALS.gift_with_purchase),
              );
            }}
          >
            {i18n.t(LOCALS.gift_with_purchase)}
          </Button>
        </div>
      </Modal>

      <Modal
        title={i18n.t(LOCALS.adjust_promotion)}
        open={adjustPromotionModalOpen}
        onCancel={() => setAdjustPromotionModalOpen(false)}
        onOk={async () => {
          const currentPromotion =
            omsOrderDetail.promotionAmountActualCurrency ??
            omsOrderDetail.promotionAmount ??
            0;
          const isPaidStage =
            status === ORDER_STATUS_MAP.TO_BE_DELIVERED ||
            status === ORDER_STATUS_MAP.DELIVERED;
          if (isPaidStage && adjustPromotionValue <= currentPromotion) {
            message.error(i18n.t(LOCALS.adjust_promotion_paid_only_increase));
            return;
          }

          setAdjustPromotionLoading(true);
          try {
            await adjustOrderPromotion(id, {
              promotionAmountActualCurrency: adjustPromotionValue,
            });
            message.success('优惠金额已更新');
            setAdjustPromotionModalOpen(false);
            window.location.reload();
          } catch (e: unknown) {
            message.error((e as Error)?.message ?? '调整失败');
          } finally {
            setAdjustPromotionLoading(false);
          }
        }}
        confirmLoading={adjustPromotionLoading}
        okText={i18n.t(LOCALS.confirm)}
        cancelText={i18n.t(LOCALS.cancel)}
      >
        <div className="py-2">
          <div className="text-sm text-gray-500 mb-2">
            <Trans i18nKey={LOCALS.enter_discount_amount} />
          </div>
          {(status === ORDER_STATUS_MAP.TO_BE_DELIVERED ||
            status === ORDER_STATUS_MAP.DELIVERED) && (
            <div className="text-sm text-orange-500 mb-2">
              <Trans i18nKey={LOCALS.adjust_promotion_paid_hint} />
            </div>
          )}
          <InputNumber
            className="w-full"
            min={
              status === ORDER_STATUS_MAP.TO_BE_DELIVERED ||
              status === ORDER_STATUS_MAP.DELIVERED
                ? (omsOrderDetail.promotionAmountActualCurrency ??
                  omsOrderDetail.promotionAmount ??
                  0)
                : 0
            }
            max={
              omsOrderDetail.totalAmountActualCurrency != null
                ? omsOrderDetail.totalAmountActualCurrency
                : omsOrderDetail.totalAmount
            }
            value={adjustPromotionValue}
            onChange={(v) => setAdjustPromotionValue(v ?? 0)}
            addonBefore={
              omsOrderDetail.orderItemList?.[0]?.actualCurrency || 'JPY'
            }
          />
        </div>
      </Modal>

      <Modal
        title={i18n.t(LOCALS.adjust_freight)}
        open={adjustFreightModalOpen}
        onCancel={() => setAdjustFreightModalOpen(false)}
        onOk={async () => {
          if (adjustFreightValue < 0) {
            message.error(i18n.t(LOCALS.enter_freight_amount));
            return;
          }

          setAdjustFreightLoading(true);
          try {
            await adjustOrderFreight(id, {
              freightAmountActualCurrency: adjustFreightValue,
            });
            message.success(i18n.t(LOCALS.adjust_freight_success));
            setAdjustFreightModalOpen(false);
            window.location.reload();
          } catch (e: unknown) {
            message.error((e as Error)?.message ?? '调整失败');
          } finally {
            setAdjustFreightLoading(false);
          }
        }}
        confirmLoading={adjustFreightLoading}
        okText={i18n.t(LOCALS.confirm)}
        cancelText={i18n.t(LOCALS.cancel)}
      >
        <div className="py-2">
          <div className="text-sm text-gray-500 mb-2">
            <Trans i18nKey={LOCALS.enter_freight_amount} />
          </div>
          <div className="text-sm text-orange-500 mb-2">
            <Trans i18nKey={LOCALS.adjust_freight_hint} />
          </div>
          <InputNumber
            className="w-full"
            min={0}
            precision={
              (omsOrderDetail.orderItemList?.[0]?.actualCurrency || 'JPY') ===
              'JPY'
                ? 0
                : 2
            }
            value={adjustFreightValue}
            onChange={(v) => setAdjustFreightValue(v ?? 0)}
            addonBefore={
              omsOrderDetail.orderItemList?.[0]?.actualCurrency || 'JPY'
            }
          />
        </div>
      </Modal>

      <Modal
        title={i18n.t(LOCALS.convert_to_tax_inclusive)}
        open={convertTaxModalOpen}
        onCancel={() => {
          setConvertTaxModalOpen(false);
          setConvertTaxPreview(null);
        }}
        onOk={async () => {
          if (!convertTaxPreview) return;
          setConvertTaxLoading(true);
          try {
            await convertOrderToTaxInclusive(id);
            message.success(i18n.t(LOCALS.convert_to_tax_inclusive_success));
            setConvertTaxModalOpen(false);
            window.location.reload();
          } catch (e: unknown) {
            message.error((e as Error)?.message ?? '操作失败');
          } finally {
            setConvertTaxLoading(false);
          }
        }}
        confirmLoading={convertTaxLoading}
        okButtonProps={{ disabled: !convertTaxPreview }}
        okText={i18n.t(LOCALS.confirm)}
        cancelText={i18n.t(LOCALS.cancel)}
      >
        <Spin spinning={convertTaxPreviewLoading}>
          <div className="py-2 space-y-3">
            <div className="text-sm text-orange-500">
              <Trans i18nKey={LOCALS.convert_to_tax_inclusive_hint} />
            </div>
            {convertTaxPreview && (
              <>
                <div className="text-sm space-y-1">
                  <div>
                    <Trans i18nKey={LOCALS.amount_to_be_paid} />:{' '}
                    {convertTaxPreview.currency}{' '}
                    {convertTaxPreview.oldPayAmountActualCurrency.toLocaleString()}{' '}
                    → {convertTaxPreview.currency}{' '}
                    {convertTaxPreview.newPayAmountActualCurrency.toLocaleString()}
                  </div>
                  <div>
                    <Trans i18nKey={LOCALS.tax_amount} />:{' '}
                    {convertTaxPreview.currency}{' '}
                    {convertTaxPreview.oldTotalTaxAmount.toLocaleString()} →{' '}
                    {convertTaxPreview.currency}{' '}
                    {convertTaxPreview.newTotalTaxAmount.toLocaleString()}
                  </div>
                  <div className="font-medium">
                    <Trans i18nKey={LOCALS.tax_difference} />:{' '}
                    {convertTaxPreview.currency}{' '}
                    {convertTaxPreview.taxDiff.toLocaleString()}
                  </div>
                </div>
                {convertTaxPreview.items.map((item) => (
                  <div key={item.productId} className="text-sm text-gray-600">
                    {item.productName} × {item.productQuantity}：
                    {convertTaxPreview.currency}{' '}
                    {item.oldProductPrice.toLocaleString()} →{' '}
                    {convertTaxPreview.currency}{' '}
                    {item.newProductPrice.toLocaleString()}
                  </div>
                ))}
              </>
            )}
          </div>
        </Spin>
      </Modal>

      <Modal
        title={i18n.t(LOCALS.append_use_integration)}
        open={appendIntegrationModalOpen}
        onCancel={() => setAppendIntegrationModalOpen(false)}
        onOk={async () => {
          if (!appendIntegrationValue || appendIntegrationValue <= 0) {
            message.error(i18n.t(LOCALS.enter_append_use_points));
            return;
          }

          setAppendIntegrationLoading(true);
          try {
            await adjustOrderUseIntegration(id, {
              additionalUseIntegration: appendIntegrationValue,
            });
            message.success('积分抵扣已更新');
            setAppendIntegrationModalOpen(false);
            window.location.reload();
          } catch (e: unknown) {
            message.error((e as Error)?.message ?? '操作失败');
          } finally {
            setAppendIntegrationLoading(false);
          }
        }}
        confirmLoading={appendIntegrationLoading}
        okText={i18n.t(LOCALS.confirm)}
        cancelText={i18n.t(LOCALS.cancel)}
      >
        <div className="py-2">
          <div className="text-sm text-gray-500 mb-2">
            <Trans i18nKey={LOCALS.enter_append_use_points} />
          </div>
          <div className="text-sm text-gray-500 mb-2">
            {i18n.t(LOCALS.used_points)}: JPY{' '}
            {currentUseIntegration.toLocaleString()}
            {memberAvailableIntegration != null && (
              <>
                {' '}
                / {i18n.t(LOCALS.available_points)}: JPY{' '}
                {memberAvailableIntegration.toLocaleString()}
              </>
            )}
          </div>
          <InputNumber
            className="w-full"
            min={1}
            max={maxAppendIntegration > 0 ? maxAppendIntegration : undefined}
            precision={0}
            value={appendIntegrationValue}
            onChange={(v) => setAppendIntegrationValue(v)}
            addonBefore="JPY"
            placeholder={i18n.t(LOCALS.enter_append_use_points) || ''}
          />
          {maxAppendIntegration > 0 && (
            <div className="text-xs text-gray-400 mt-2">
              最多可追加 JPY {maxAppendIntegration.toLocaleString()}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={closeOrderModalOpen}
        title={<Trans i18nKey={LOCALS.close_order} />}
        onCancel={() => {
          setCloseOrderModalOpen(false);
          closeOrderForm.resetFields();
        }}
        onOk={onCloseOrderOk}
      >
        {(ORDER_STATUS_MAP.TO_BE_DELIVERED === status ||
          ORDER_STATUS_MAP.DELIVERED === status) && (
          <div style={{ color: 'red', marginBottom: 6 }}>
            <Trans i18nKey={LOCALS.bzsNZmtrmS} />
          </div>
        )}

        <Form form={closeOrderForm} initialValues={{ isSendEmail: true }}>
          <Form.Item
            name="refundMethod"
            label={<Trans i18nKey={LOCALS.rLrlFnIYtn} />}
            rules={[
              {
                required: status !== ORDER_STATUS_MAP.TO_BE_PAID,
                message: <Trans i18nKey={LOCALS.please_enter} />,
              },
            ]}
            hidden={status === ORDER_STATUS_MAP.TO_BE_PAID}
          >
            <Input.TextArea placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
          <Form.Item
            name="note"
            label={<Trans i18nKey={LOCALS.remark} />}
            // rules={[{ required: true }]}
          >
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter_remark) || ''}
            />
          </Form.Item>
          <Form.Item
            name="isSendEmail"
            label={i18n.t('HVTbgZaqUs')}
            valuePropName="checked"
            hidden={status === ORDER_STATUS_MAP.TO_BE_PAID}
          >
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>

      {finalSettlementModalOpen && (
        <FinalSettlement
          open={finalSettlementModalOpen}
          onCancel={(b) => toggleFinalSettlementModalOpen(b)}
          omsOrderDetail={omsOrderDetail}
        />
      )}

      <Modal
        title={<Trans i18nKey={LOCALS.UFbYgUaLbn} />}
        open={bindMemberModalOpen}
        onCancel={() => {
          setBingMemberModalOpen(false);
          setSelectedMemberId(undefined);
          bindMemberForm.resetFields();
        }}
        onOk={onBindMemberOk}
      >
        <Form
          form={bindMemberForm}
          labelCol={{ span: 6 }}
          initialValues={{
            isPointsGiven: true,
          }}
        >
          <Form.Item label={<Trans i18nKey={LOCALS.select_member} />}>
            <SimpleMemberSelect
              onChange={setSelectedMemberId}
              value={selectedMemberId}
            />
          </Form.Item>

          <Form.Item
            name="isPointsGiven"
            label={<Trans i18nKey={LOCALS.points_earned} />}
          >
            <Radio.Group>
              <Radio value={true}>
                <Trans i18nKey={LOCALS.yes} />
              </Radio>
              <Radio value={false}>
                <Trans i18nKey={LOCALS.no} />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OrderOperate;
